import { useEffect, useRef, useState } from "react";
import { useLoading} from "../context/LoadingContext";

const LoadingIndicator = () => {
  const { loading, message, color, progress: realProgress } = useLoading();
  const [visible, setVisible] = useState(false);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [status, setStatus] = useState("Processing");
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const hasRealProgress = typeof realProgress === "number";
  const progress = hasRealProgress ? realProgress : simulatedProgress;

  useEffect(() => {
    if (loading) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setVisible(true);
      setStatus("Processing");

      // Real progress (from a background job over WS) replaces the
      // simulated bar entirely — no point faking numbers we actually know.
      if (hasRealProgress) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      setSimulatedProgress(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        setSimulatedProgress((prev) => {
          if (prev >= 90) return prev;
          const next = prev + Math.floor(Math.random() * 6) + 3;
          return Math.min(next, 90);
        });
      }, 400);
      return;
    }

    if (visible) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setSimulatedProgress(100);
      setStatus("Done");
      timeoutRef.current = setTimeout(() => {
        setVisible(false);
        timeoutRef.current = null;
      }, 500);
    }
  }, [loading, visible, hasRealProgress]);

  useEffect(() => () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

return (
    <>
      {visible && (
        <>
          <div className="pointer-events-auto fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-[1px]"></div>

          <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
            <div className="w-full max-w-lg rounded-2xl border border-violet-200 bg-white/95 p-6 shadow-2xl">
              <div className="flex items-center gap-4">
                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-50">
                  <span className="loading loading-spinner loading-md text-violet-600"></span>
                  <span className="absolute h-3 w-3 rounded-full bg-violet-400 opacity-60 animate-ping"></span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-500">{status}</div>
                  <div className={`mt-1 text-lg font-semibold text-black ${color}`}>{message}</div>
                </div>
                <div className="text-sm font-semibold text-slate-600">{Math.round(progress)}%</div>
              </div>
              <div className="mt-5">
                <div className="h-2 w-full overflow-hidden rounded-full bg-violet-100">
                  <div
                    className="h-full rounded-full bg-violet-500 transition-[width] duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  Please keep this tab open while we finish.
                </div>
              </div>
            </div>
          </div>

          {/* Disable interactions globally */}
          <style>
            {`body { cursor: ${loading ? "wait" : "auto"}; overflow: hidden; }`}
          </style>
        </>
      )}
    </>
  );
};

export default LoadingIndicator;
